import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sprout, CalendarDays, Scissors, Sparkles, MapPin, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_TO_ABBR = {
  January: 'Jan', February: 'Feb', March: 'Mar', April: 'Apr', May: 'May', June: 'Jun',
  July: 'Jul', August: 'Aug', September: 'Sep', October: 'Oct', November: 'Nov', December: 'Dec',
};

export function useRegionalGuide(cropId) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/crops/${cropId}/regional-guide`, { withCredentials: true })
      .then((r) => { if (active) setGuide(r.data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cropId]);
  return { guide, loading };
}

function MonthChart({ guide }) {
  const sow = new Set((guide.sowing_months || []).map((m) => FULL_TO_ABBR[m]));
  const tp = new Set((guide.transplant_months || []).map((m) => FULL_TO_ABBR[m]));
  const harv = new Set((guide.harvest_months || []).map((m) => FULL_TO_ABBR[m]));
  const nowAbbr = FULL_TO_ABBR[guide.current_month];
  return (
    <div data-testid="regional-month-chart">
      <div className="grid grid-cols-12 gap-1">
        {MONTHS.map((m) => {
          const isNow = m === nowAbbr;
          return (
            <div key={m} className="flex flex-col items-center gap-0.5">
              <span className={`text-[10px] font-semibold ${isNow ? 'text-[#1A4331]' : 'text-[#9AA79A]'}`}>{m[0]}</span>
              <div className={`w-full rounded-md overflow-hidden flex flex-col ${isNow ? 'ring-2 ring-[#1A4331]' : ''}`} style={{ height: '46px' }}>
                <div className={`flex-1 ${sow.has(m) ? 'bg-[#6FA22E]' : 'bg-[#EEF1E8]'}`} title="Sow seeds" />
                <div className={`flex-1 ${tp.has(m) ? 'bg-[#8EAA97]' : 'bg-[#EEF1E8]'}`} title="Plant seedlings" />
                <div className={`flex-1 ${harv.has(m) ? 'bg-[#D4AF37]' : 'bg-[#EEF1E8]'}`} title="Harvest" />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-[#5C665C]">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#6FA22E]" /> Sow seeds</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#8EAA97]" /> Plant seedlings</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#D4AF37]" /> Harvest</span>
      </div>
    </div>
  );
}

// Compact sidebar chart — "Sowing / Transplant Guide"
export function SowingGuideSidebar({ cropId }) {
  const { guide, loading } = useRegionalGuide(cropId);
  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm flex justify-center">
        <Loader2 className="w-6 h-6 text-[#8EAA97] animate-spin" />
      </div>
    );
  }
  if (!guide) return null;
  return (
    <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm lg:sticky lg:top-6" data-testid="sowing-guide-sidebar">
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays className="w-5 h-5 text-[#1A4331]" />
        <h3 className="font-semibold text-[#1A4331] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Sowing / Transplant Guide</h3>
      </div>
      <p className="text-xs text-[#5C665C] flex items-center gap-1 mb-4">
        <MapPin className="w-3 h-3" /> {guide.location || guide.zone_region} · {guide.zone_label} zone
      </p>

      <MonthChart guide={guide} />

      <div className="mt-5 space-y-3">
        <GuideRow icon={<Sprout className="w-4 h-4 text-[#6FA22E]" />} label="Sow seeds" value={guide.sowing_range} testid="guide-sow" />
        <GuideRow icon={<Sprout className="w-4 h-4 text-[#8EAA97]" />} label="Plant seedlings" value={guide.transplant_range === '—' ? 'Direct sow only' : guide.transplant_range} testid="guide-transplant" />
        <GuideRow icon={<Scissors className="w-4 h-4 text-[#D4AF37]" />} label="Harvest" value={guide.harvest_range} testid="guide-harvest" />
        <GuideRow icon={<CalendarDays className="w-4 h-4 text-[#1A4331]" />} label="Time to maturity" value={guide.days_to_maturity_range ? `${guide.days_to_maturity_range[0]}–${guide.days_to_maturity_range[1]} days · ~${guide.days_to_maturity} likely` : (guide.weeks_to_maturity ? `~${guide.weeks_to_maturity} weeks (${guide.days_to_maturity} days)` : '—')} testid="guide-maturity" />
      </div>

      <div className={`mt-4 rounded-2xl px-4 py-3 text-sm font-semibold ${guide.currently_sowable ? 'bg-[#6FA22E]/10 text-[#1A4331]' : 'bg-[#F0F2EB] text-[#5C665C]'}`} data-testid="sow-now-status">
        {guide.currently_sowable
          ? `✓ Safe to sow now (${guide.current_month})`
          : guide.next_sow_month
            ? `Next safe sowing: ${guide.next_sow_month}`
            : 'Sowing times vary — see guide'}
      </div>
    </div>
  );
}

function GuideRow({ icon, label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3" data-testid={testid}>
      <span className="flex items-center gap-2 text-sm text-[#5C665C]">{icon} {label}</span>
      <span className="text-sm font-semibold text-[#1A4331] text-right">{value}</span>
    </div>
  );
}

// Main growing-guide card (replaces the old basic Planting Calendar)
export function RegionalGuideCard({ cropId }) {
  const { guide, loading } = useRegionalGuide(cropId);
  const [aiText, setAiText] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(false);

  useEffect(() => { if (guide?.ai_guide) setAiText(guide.ai_guide); }, [guide]);

  const askAi = async () => {
    setAiLoading(true);
    setAiError(false);
    try {
      const r = await axios.post(`${API}/crops/${cropId}/regional-guide/ai`, {}, { withCredentials: true });
      setAiText(r.data.text);
    } catch (e) { setAiError(true); } finally { setAiLoading(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm flex justify-center">
        <Loader2 className="w-6 h-6 text-[#8EAA97] animate-spin" />
      </div>
    );
  }
  if (!guide) return null;

  return (
    <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="regional-guide-card">
      <h2 className="text-2xl font-semibold text-[#1A4331] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Your regional growing guide
      </h2>
      <p className="text-sm text-[#5C665C] mb-4 flex items-center gap-1">
        <MapPin className="w-4 h-4" /> Tailored to {guide.location || guide.zone_region} · {guide.zone_label} zone
      </p>

      <p className="text-[#2D241E] leading-relaxed bg-[#F0F2EB] rounded-2xl p-4" data-testid="regional-plain-summary">
        {guide.plain_summary}
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <Stat color="#6FA22E" icon={<Sprout className="w-4 h-4" />} label="Sow seeds" value={guide.sowing_range} />
        <Stat color="#8EAA97" icon={<Sprout className="w-4 h-4" />} label="Plant seedlings" value={guide.transplant_range === '—' ? 'Direct sow only' : guide.transplant_range} />
        <Stat color="#D4AF37" icon={<Scissors className="w-4 h-4" />} label="Harvest" value={guide.harvest_range} />
      </div>

      <div className="mt-5 pt-5 border-t border-[#1A4331]/5">
        {aiText ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#1A4331]" />
              <span className="text-sm font-semibold text-[#1A4331]">AI-tailored for your patch</span>
            </div>
            <div className="text-sm text-[#2D241E] leading-relaxed whitespace-pre-line" data-testid="regional-ai-text">
              {renderMarkdown(aiText)}
            </div>
            <button onClick={askAi} disabled={aiLoading} data-testid="regenerate-ai-guide" className="mt-3 text-xs font-semibold text-[#5C665C] hover:text-[#1A4331]">
              {aiLoading ? 'Refreshing…' : 'Regenerate'}
            </button>
          </div>
        ) : (
          <button onClick={askAi} disabled={aiLoading} data-testid="ask-ai-guide-button"
            className="inline-flex items-center gap-2 bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-2.5 px-5 rounded-full transition-colors disabled:opacity-60">
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {aiLoading ? 'Writing your guide…' : 'Ask AI to tailor this to my garden'}
          </button>
        )}
        {aiError && (
          <p className="text-sm text-[#E2725B] mt-2" data-testid="ai-guide-error">Couldn&apos;t reach the AI just now — please try again.</p>
        )}
      </div>
    </div>
  );
}

function Stat({ color, icon, label, value }) {
  return (
    <div className="rounded-2xl p-4" style={{ backgroundColor: `${color}1a` }}>
      <div className="flex items-center gap-2 mb-1" style={{ color }}>{icon}<span className="text-xs font-semibold">{label}</span></div>
      <p className="text-base font-bold text-[#1A4331]">{value}</p>
    </div>
  );
}

// tiny markdown: # headers, **bold**, and bullet lines
function renderMarkdown(text) {
  return text.split('\n').map((line, i) => {
    let trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-2" />;
    const heading = trimmed.match(/^#{1,3}\s+(.*)$/);
    if (heading) return <p key={i} className="font-bold text-[#1A4331] text-base mt-3 mb-1">{heading[1].replace(/\*\*/g, '')}</p>;
    const boldOnly = trimmed.match(/^\*\*(.+?)\*\*:?$/);
    if (boldOnly) return <p key={i} className="font-bold text-[#1A4331] mt-3 mb-1">{boldOnly[1]}</p>;
    const isBullet = trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.startsWith('* ');
    if (isBullet) trimmed = trimmed.replace(/^[-•*]\s+/, '');
    const parts = trimmed.split(/(\*\*.+?\*\*)/g).map((p, j) => {
      const b = p.match(/^\*\*(.+?)\*\*$/);
      return b ? <strong key={j}>{b[1]}</strong> : <span key={j}>{p}</span>;
    });
    return <p key={i} className={isBullet ? 'ml-4 relative before:content-["•"] before:absolute before:-left-4 before:text-[#8EAA97]' : ''}>{parts}</p>;
  });
}
